import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, fieldErrors, send } from '../lib/api';
import { focusFirstError, readForm, textField } from '../lib/form';
import { icon } from '../lib/icons';
import { LightElement } from '../lib/light-element';
import { appUrl } from '../lib/page';
import './hl-password-field';

const minPasswordLength = 12;

/** Access request form. Posts to /api/account/register; an admin approves it later. */
@customElement('hl-register-form')
export class HlRegisterForm extends LightElement {
  @state() private busy = false;

  @state() private submitted = false;

  @state() private error = '';

  @state() private errors: Record<string, string> = {};

  private async onSubmit(e: SubmitEvent) {
    e.preventDefault();
    const values = readForm(e.currentTarget as HTMLFormElement) as Record<string, string>;

    const errors: Record<string, string> = {};
    if (!values.displayName?.trim()) errors.displayName = 'Enter your name.';
    if (!/^[A-Za-z0-9._-]{3,64}$/.test(values.userName?.trim() ?? '')) {
      errors.userName = 'Use 3–64 letters, numbers, dots, dashes or underscores.';
    }
    if ((values.password ?? '').length < minPasswordLength) errors.password = `Use at least ${minPasswordLength} characters.`;
    this.errors = errors;
    this.error = '';
    if (Object.keys(errors).length > 0) {
      await this.updateComplete;
      focusFirstError(this, errors);
      return;
    }

    this.busy = true;
    const result = await send(() =>
      api().POST('/api/account/register', {
        body: {
          userName: values.userName.trim(),
          displayName: values.displayName.trim(),
          email: values.email?.trim() || null,
          password: values.password,
        },
      }));
    this.busy = false;
    if (result.ok) {
      this.submitted = true;
      return;
    }

    this.errors = fieldErrors(result.problem);
    this.error = Object.keys(this.errors).length ? '' : (result.problem.title ?? '');
    await this.updateComplete;
    focusFirstError(this, this.errors);
  }

  override render() {
    if (this.submitted) {
      return html`
        <div class="hl-auth-done" role="status" data-testid="register-success">
          <span class="hl-auth-done-icon">${icon('check', 30, 2)}</span>
          <h1 class="hl-auth-title">Request sent</h1>
          <p class="hl-auth-lead">You can sign in once an administrator approves it.</p>
        </div>
        <a class="btn btn-secondary hl-btn-block" href=${appUrl('login')}>Back to sign in</a>
      `;
    }

    return html`
      <div class="hl-auth-head">
        <h1 class="hl-auth-title" data-testid="page-title">Request access</h1>
        <p class="hl-auth-lead">An administrator will review your request.</p>
      </div>
      <form class="hl-form" novalidate @submit=${this.onSubmit}>
        ${textField({ id: 'register-name', name: 'displayName', label: 'Your name', autocomplete: 'name', error: this.errors.displayName })}
        ${textField({
          id: 'register-username',
          name: 'userName',
          label: 'Username',
          autocomplete: 'username',
          help: 'Letters, numbers, dots, dashes or underscores.',
          error: this.errors.userName,
        })}
        ${textField({ id: 'register-email', name: 'email', label: 'Email', type: 'email', autocomplete: 'email', optional: true, error: this.errors.email })}
        <hl-password-field
          input-id="register-password"
          name="password"
          autocomplete="new-password"
          help="At least ${minPasswordLength} characters."
          .error=${this.errors.password ?? ''}
        ></hl-password-field>
        <p class="hl-error hl-callout-error" role="alert" data-testid="register-error">${this.error}</p>
        <button type="submit" class="btn btn-primary hl-btn-block" ?disabled=${this.busy} data-testid="register-submit">
          ${this.busy ? 'Sending…' : 'Send request'}
        </button>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hl-register-form': HlRegisterForm;
  }
}
